/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
// You can delete this file if you're not using it
import './src/shims/babel-polyfill-shim';
import { Amplify, Auth } from 'aws-amplify';
import awsConfig from "./src/aws-exports"
awsConfig.oauth.domain = process.env.GATSBY_COGNITO_OAUTH_DOMAIN;
// import core components
import { cartItemsService } from 'services';    
import UserContextProvider from "./src/contexts/UserContextProvider";

Amplify.configure(awsConfig)

document.addEventListener("snipcart.ready", cartItemsService.onCartReady);

const PILL_STYLE_NODE_ID = 'ss-snipcart-pill-style';
const PILL_CLASS = 'ss-pill-bs';
const PILL_SELECTOR = [
  '#snipcart button.snipcart-checkout',
  '#snipcart button.snipcart-cart__checkout-button',
  '#snipcart button.snipcart-mainaction',
  '#snipcart button.snipcart-submit',
  '#snipcart .snipcart-submit',
  '#snipcart .snipcart-button-primary',
  '#snipcart button[type="submit"]',
  '#snipcart input[type="submit"]',
  '#snipcart-main-container button.snipcart-checkout',
  '#snipcart-main-container button.snipcart-cart__checkout-button',
  '#snipcart-main-container button.snipcart-mainaction',
  '#snipcart-main-container button.snipcart-submit',
  '#snipcart-main-container .snipcart-submit',
  '#snipcart-main-container .snipcart-button-primary',
  '#snipcart-main-container button[type="submit"]',
  '#snipcart-main-container input[type="submit"]'
].join(', ');

const ensurePillStylesheet = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PILL_STYLE_NODE_ID)) return;

  const style = document.createElement('style');
  style.id = PILL_STYLE_NODE_ID;
  style.textContent = `
    #snipcart .${PILL_CLASS},
    #snipcart-main-container .${PILL_CLASS} {
      border-radius: 999px !important;
      padding: 0.75rem 1.2rem !important;
      background: #1b3a4b !important;
      color: #fff !important;
      border: none !important;
      box-shadow: 0 10px 32px rgba(27, 58, 75, 0.25) !important;
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      font-weight: 600 !important;
      letter-spacing: 0.01em;
      transition: transform .12s ease, filter .12s ease !important;
    }
    #snipcart .${PILL_CLASS}:hover,
    #snipcart-main-container .${PILL_CLASS}:hover {
      filter: brightness(1.05) !important;
      transform: translateY(-1px) !important;
    }
    #snipcart .${PILL_CLASS}:active,
    #snipcart-main-container .${PILL_CLASS}:active {
      transform: translateY(0) !important;
    }
    #snipcart .${PILL_CLASS} .snipcart-cart-button__icon path,
    #snipcart-main-container .${PILL_CLASS} .snipcart-cart-button__icon path {
      fill: #fff !important;
    }
  `;
  document.head.appendChild(style);
};

const applyInlinePillStyles = (button) => {
  if (!button) return;
  try {
    button.style.setProperty('border-radius', '999px', 'important');
    button.style.setProperty('padding', '0.75rem 1.2rem', 'important');
    button.style.setProperty('background', '#1b3a4b', 'important');
    button.style.setProperty('background-image', 'none', 'important');
    button.style.setProperty('color', '#ffffff', 'important');
    button.style.setProperty('border', '0', 'important');
    button.style.setProperty('box-shadow', '0 10px 32px rgba(27, 58, 75, 0.25)', 'important');
    button.style.setProperty('display', 'inline-flex', 'important');
    button.style.setProperty('align-items', 'center', 'important');
    button.style.setProperty('justify-content', 'center', 'important');
    button.style.setProperty('gap', '0.4rem', 'important');
    button.style.setProperty('font-weight', '600', 'important');
    button.style.setProperty('letter-spacing', '0.01em', 'important');
    button.style.setProperty('transition', 'transform .12s ease, filter .12s ease', 'important');
  } catch (err) {
    // swallow styling errors silently
  }
};

const applyPillStyles = () => {
  if (typeof document === 'undefined') return;
  ensurePillStylesheet();
  const buttonList = new Set();
  document.querySelectorAll(PILL_SELECTOR).forEach((btn) => buttonList.add(btn));

  // heuristic: catch translated versions of "Place Order" or similar CTAs
  const keywordMatchers = ['place order', 'complete order', 'confirm order', 'pay now', 'finish order'];
  const textualButtons = document.querySelectorAll('#snipcart-main-container button, #snipcart button');
  textualButtons.forEach((btn) => {
    const text = (btn.textContent || '').toLowerCase();
    if (keywordMatchers.some((kw) => text.includes(kw))) {
      buttonList.add(btn);
    }
  });

  if (!buttonList.size) return;

  buttonList.forEach((btn) => {
    if (!btn.classList.contains('snipcart-cart-button')) {
      btn.classList.add('snipcart-cart-button');
    }
    if (!btn.classList.contains(PILL_CLASS)) {
      btn.classList.add(PILL_CLASS);
    }
    if (btn.tagName === 'INPUT') {
      // convert input submit to behave like button via inline styles
      btn.style.setProperty('appearance', 'none', 'important');
    }
    applyInlinePillStyles(btn);
  });
};

const attachSnipcartObserver = () => {
  if (typeof MutationObserver === 'undefined') return;
  if (window.__ssSnipcartPillObserver) return;

  const target = document.getElementById('snipcart-main-container')
    || document.getElementById('snipcart')
    || document.body;
  if (!target) return;

  const observer = new MutationObserver(() => {
    applyPillStyles();
  });

  observer.observe(target, { childList: true, subtree: true });
  window.__ssSnipcartPillObserver = observer;
};

const schedulePillUpdate = () => {
  if (typeof window === 'undefined') return;
  applyPillStyles();
  attachSnipcartObserver();
  requestAnimationFrame(applyPillStyles);
  setTimeout(() => {
    applyPillStyles();
    attachSnipcartObserver();
  }, 100);
  setTimeout(() => {
    applyPillStyles();
    attachSnipcartObserver();
  }, 400);
  setTimeout(() => {
    applyPillStyles();
    attachSnipcartObserver();
  }, 1000);
};

document.addEventListener('snipcart.ready', () => {
  schedulePillUpdate();

  const { Snipcart } = window;
  if (Snipcart?.events) {
    const events = ['cart.opened', 'cart.closed', 'cart.confirmed', 'theme.routechanged'];
    events.forEach((evt) => {
      Snipcart.events.on(evt, schedulePillUpdate);
    });
  }
  if (Snipcart?.store) {
    Snipcart.store.subscribe(schedulePillUpdate);
  }
});

export const wrapRootElement = ({ element }) => (
    <UserContextProvider>{element}</UserContextProvider>
  )
